import clsx, { ClassValue } from "clsx";
import { IsUncertain, SN } from "../@types/global";
import { isValidElement } from "react";

export function detectCard(event: any) {
  const rawValue = event.target.value.replace(/\s/g, "");
  //   const maskedValue = rawValue.replace(/(\d{1,4})/g, "$1 ").trim();

  // Detect card type
  if (/^4/.test(rawValue)) {
    return "Visa";
  } else if (/^5[1-5]/.test(rawValue)) {
    return "Mastercard";
  } else if (/^3[47]/.test(rawValue)) {
    return "American Express";
  } else if (/^6(?:011|5)/.test(rawValue)) {
    return "Discover";
  } else if (/^5[6-9]|^506[0-9]|^6500[0-9]/.test(rawValue)) {
    return "Verve";
  } else {
    return "";
  }
}

export function formatCard(event: any) {
  const rawValue = event.target.value.replace(/\s/g, "");

  return rawValue.replace(/(\d{1,4})/g, "$1 ").trim();
}

export function handleCvvChange(event: any) {
  // Get the input value without spaces
  const rawValue = event.target.value.replace(/\s/g, "");

  // Replace all digits with asterisks except the last 3
  const maskedValue = rawValue.replace(/\d(?=\d{3})/g, "*");
  // Set the input value to the masked version
  return maskedValue;
}

export function handleExpiryDateChange(event: any) {
  // Get the input value without slashes
  const rawValue = event.target.value.replace(/\//g, "");

  // Add slash after the first two digits
  const maskedValue = rawValue.replace(/^(\d{1,2})/, "$1/").substr(0, 7);

  // Set the input value to the masked version
  return maskedValue;
}

interface FormatOptions {
  hideDecimal?: boolean;
  hideSymbol?: boolean;
  convertToNumber?: boolean;
  symbol?: string;
  removeLeadingZero?: boolean;
  numberAbbreviate?: boolean;
}
const formatNumberWithSymbols = (number: number) => {
  const formats = [
    { value: 1, symbol: "" },
    { value: 1e3, symbol: "k" },
    { value: 1e6, symbol: "M" },
    { value: 1e9, symbol: "B" },
    { value: 1e12, symbol: "T" },
    { value: 1e15, symbol: "Q" },
    { value: 1e18, symbol: "QT" },
    { value: 1e21, symbol: "QQ" },
  ];
  const format = formats
    .slice()
    .reverse()
    .find((fmt) => number >= fmt.value);
  if (format) {
    return (
      (number / format.value).toFixed(2).replace(/\.00$/, "") + format.symbol
    );
  }
  return number.toString();
};

const getCurrencySymbol = (param: string) => {
  const nairaSymbol = "\u{020A6}";
  const upperParam = String(param).toUpperCase();
  return upperParam === "NGN" || upperParam === "NAIRA"
    ? nairaSymbol
    : upperParam === "USD" || upperParam === "DOLLAR"
    ? "$"
    : upperParam === "EUR" || upperParam === "EURO"
    ? "€"
    : upperParam === "ZAR"
    ? "R"
    : upperParam === "KES"
    ? "KSh"
    : upperParam === "CAD"
    ? "$"
    : upperParam === "GHS"
    ? "₵"
    : upperParam === "GBP" || upperParam === "POUNDS" || upperParam === "POUND"
    ? "£"
    : param;
};

export const RazorNumberFormat = (
  number: number | string | null | undefined,
  options?: FormatOptions
): string => {
  const symbol = getCurrencySymbol(options?.symbol || "NGN");
  const hideDecimal = options?.hideDecimal || false;
  const hideSymbol = options?.hideSymbol || false;
  const convertToNumber = options?.convertToNumber || false;
  const removeLeadingZero = options?.removeLeadingZero || false;
  const numberAbbreviate = options?.numberAbbreviate || false;
  if (number !== null && number !== undefined) {
    // Remove symbols and non-numeric characters
    const numericValue: string = number.toString().replace(/[^0-9.]/g, "");
    // Separate the integer and decimal parts
    let [integerPart, decimalPart] = numericValue.split(".");
    if (removeLeadingZero) {
      // Remove leading zeros if more than one leading zero
      if (/^0{2,}/.test(integerPart)) {
        integerPart = integerPart.replace(/^0+/, "");
      }
    }
    if (convertToNumber) {
      // Return number without thousand formatting and without symbol
      const val = decimalPart ? `${integerPart}.${decimalPart}` : integerPart;
      return val;
    }
    const formattedValue = Number(
      integerPart + (decimalPart ? "." + decimalPart : "")
    );
    if (numberAbbreviate) {
      const formatted = formatNumberWithSymbols(formattedValue);
      return hideSymbol ? formatted : `${symbol}${formatted}`;
    }
    // Format the integer part with thousand separator
    integerPart = Number(integerPart).toLocaleString();
    // Handle the decimal part
    if (!hideDecimal) {
      if (decimalPart) {
        // Convert the decimal part back to a number and round to two significant figures
        decimalPart = Number("0." + decimalPart)
          .toPrecision(2)
          .split(".")[1];
        // Remove trailing zeros from the decimal part
        decimalPart = decimalPart?.replace(/0+$/, "") || "";
      }
      const val = decimalPart
        ? `${integerPart}${decimalPart ? "." : ""}${decimalPart}`
        : `${integerPart}.00`;
      return hideSymbol ? val : `${symbol}${val}`;
    } else {
      return hideSymbol ? integerPart : `${symbol}${integerPart}`;
    }
  } else {
    return hideSymbol ? `0.00` : `${symbol}0.00`;
  }
};

export function RazorReturnInitial(name: string) {
  if (name) {
    const i = name?.split(" ");
    if (i.length > 1) {
      return i[0]?.slice(0, 1).toUpperCase() + i[1]?.slice(0, 1).toUpperCase();
    } else {
      return i[0]?.slice(0, 1).toUpperCase() + i[0]?.slice(1, 2).toUpperCase();
    }
  } else {
    return "";
  }
}

export const RazorTextTrim = (str: string | undefined, num: number) => {
  if (str && num) {
    const val =
      String(str)?.length > Number(num)
        ? `${String(str).slice(0, Number(num))}...`
        : str;
    return val;
  }
  return str;
};

export const cn = (...inputs: ClassValue[]) => clsx(inputs);

export function createStyleCSSProperty(name: string, gap: SN) {
  if (typeof gap === "string") return { [name]: gap } as React.CSSProperties;

  return { [name]: `${gap}rem` } as React.CSSProperties;
}

export class TypeIs {
  static string = (str: unknown): str is string => typeof str === "string";
  static any = (value: unknown) => value as any;

  /**
 *@example
 *```ts
console.log(Object.prototype.toString.call({}) = '[object Object]');
console.log(Object.prototype.toString.call([]) = '[object Array]');
console.log(Object.prototype.toString.call(null) = '[object Null]');
 *```
 * @see {@link https://bobbyhadz.com/blog/javascript-check-if-value-is-object}
 * 
 */
  static object = (value: unknown): value is object =>
    Object.prototype.toString.call(value) === "[object Object]";

  static array = (value: unknown) => Array.isArray(value);

  static number = (value: unknown): value is number =>
    typeof value === "number";

  static reactNode = (node?: unknown): node is React.ReactNode =>
    isValidElement<any>(node);

  static stringOrNumber = (value: unknown): value is string | number =>
    this.string(value) || this.number(value);

  static custom<T>(value?: unknown) {
    return value as T;
  }

  static safe = <T>(value: IsUncertain<T>) => value as T;
}

type NestedObject = { [key: string]: any };
type ValueObject = { key: string; value?: any };

/**
 * Parses a value if it is a JSON string, otherwise returns the original value.
 * @param value - The value to parse.
 * @returns The parsed value or the original value.
 */
function parseIfJsonString(value: any): any {
  if (typeof value !== "string") return value;
  try {
    const parsedValue = JSON.parse(value);
    // Check if the result is an object or an array, which are valid JSON structures
    if (typeof parsedValue === "object" && parsedValue !== null) {
      return parsedValue;
    }
    // Return the original value if parsing was successful but the result is not an object
    return value;
  } catch {
    // If an error occurs, return the original value, indicating it's not a JSON string
    return value;
  }
}

/**
 * Finds the value in a nested object based on the key path.
 * @param obj - The object to search within.
 * @param keyPath - The dot-separated path of keys to locate the value.
 * @returns The value found or an empty string if not found.
 */
function findValueInNestedObject(obj: NestedObject, keyPath: string): any {
  const keys = keyPath.split(".").map((k) => k.replace("?", ""));
  let currentValue = obj;
  for (const key of keys) {
    if (
      currentValue &&
      typeof currentValue === "object" &&
      key in currentValue
    ) {
      currentValue = parseIfJsonString(currentValue[key]);
    } else if (obj?.default) {
      return obj.default; // Return default value if key path is not found and default exists
    } else {
      return ""; // Return empty string if key path is not found
    }
  }
  return currentValue;
}
/**
 * Maps values from an array of objects based on a values object, updating empty values.
 * @param arrayOfObjects - The array of objects to map.
 * @param valuesObject - The object containing values for keys.
 * @returns An array of objects with updated values.
 */
export function RazorGetValue(
  arrayOfObjects: ValueObject[],
  valuesObject: NestedObject
): ValueObject[] {
  return arrayOfObjects.map((item) => {
    // Only update if the current value is empty or undefined
    const newValue =
      item.value === "" || item.value === undefined
        ? findValueInNestedObject(valuesObject, item.key)
        : item.value;
    return {
      ...item,
      value: newValue,
    };
  });
}
export const RazorCopyFunction = async (item: any) => {
  const flattenObject: any = (obj: any) => {
    return Object.entries(obj).flatMap(([key, value]) => {
      // Check if value is a stringified object (e.g. meta_data)
      if (typeof value === "string") {
        try {
          const parsed = JSON.parse(value);
          if (typeof parsed === "object" && parsed !== null) {
            return flattenObject(parsed); // Exclude parent key
          }
        } catch (err) {
          // Not a stringified object
          if (process.env.NODE_ENV === "development") {
            console.log(err, "RazorCopyFunction");
          }
        }
      }
      // If nested object, recurse
      if (
        typeof value === "object" &&
        value !== null &&
        !Array.isArray(value)
      ) {
        return flattenObject(value);
      }
      return `${key}: ${value}`;
    });
  };
  let formatted = "";
  if (typeof item === "string" || typeof item === "number") {
    formatted = item.toString();
  } else if (Array.isArray(item)) {
    formatted = item
      .map((obj) => {
        if (
          typeof obj === "object" &&
          obj !== null &&
          "label" in obj &&
          "value" in obj
        ) {
          return `${obj.label}: ${obj.value}`;
        }
        return "";
      })
      .filter(Boolean)
      .join("\n");
  } else if (typeof item === "object" && item !== null) {
    const flattened = flattenObject(item);
    formatted = flattened.join("\n");
  } else {
    console.error("Unsupported value for clipboard copy.");
    return;
  }
  try {
    await navigator.clipboard.writeText(formatted);
  } catch (err) {
    console.error("Clipboard write failed:", err);
  }
};

export const trimLongString = (
  str: string | undefined,
  num: number
): string => {
  if (str && num) {
    return str.length > num ? `${str.slice(0, num)}...` : str;
  }
  return ""; // Return an empty string if str is undefined or num is 0
};
